import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, IconButton, Grid } from "@mui/material";
import { Close, Cancel } from "@mui/icons-material";
import { useToast } from "../../../../../shared/hooks";
import { IVFormErros, useVForm, VForm, VTextField } from "../../../../../shared/forms";
import { IFormInstallmentCancel, IInstallmentProposal, ProposalService } from "../../../../../shared/services/api/proposal/ProposalService";
import * as yup from "yup";
import { ToastContent } from "../../../../../shared/components";
import { returnMessageError } from "../../../../../shared/utils/returnMessageError";
import { globalFormatCurrencyBRL } from "../../../../../shared/utils";
import { useState } from "react";

interface ModalCancelInstallmentsProps {
	isOpenModal: boolean;
	isClose: () => void;
	isSuperAdmin: boolean;
	row?: IInstallmentProposal;
	rows?: IInstallmentProposal[];
	onSuccess?: () => void;
}

export const ModalCancelInstallments = ({ isOpenModal, isClose, isSuperAdmin, row, rows, onSuccess }: ModalCancelInstallmentsProps) => {
	const { formRef: formRefInstallment } = useVForm();
	const toastHandler = useToast();
	const [isLoading, setIsLoading] = useState(false);

	const formValidationSchemaCancelInstallment: yup.SchemaOf<IFormInstallmentCancel> = yup.object().shape({
		obs: yup.string().required("Campo obrigatório"),
	});

	const handleSaveInstallmentCancel = async (dados: IFormInstallmentCancel) => {
		try {
			const validated = await formValidationSchemaCancelInstallment.validate(dados, {
				abortEarly: false,
			});

			const ids = row ? [row.id] : rows?.map((r) => r.id) ?? [];
			if (ids.length === 0) return;

			setIsLoading(true);
			let successCount = 0;

			for (const id of ids) {
				const result = await ProposalService.saveCancelInstallment(validated, id);
				if (!(result instanceof Error)) {
					successCount++;
				}
			}

			setIsLoading(false);

			if (successCount > 0) {
				toastHandler.present({
					type: "success",
					position: "top-right",
					messageOrContent: (
						<ToastContent
							title="Sucesso"
							content={successCount === 1 ? "Parcela cancelada com sucesso." : `${successCount} parcelas canceladas com sucesso.`}
						/>
					),
				});
				isClose();
				onSuccess?.();
			} else {
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title="Erro" content="Não foi possível cancelar as parcelas." />,
				});
			}
		} catch (errors) {
			setIsLoading(false);
			const validationErros: IVFormErros = {};
			if (errors instanceof yup.ValidationError) {
				errors.inner.forEach((error) => {
					if (!error.path) return;
					validationErros[error.path] = error.message;
				});
				formRefInstallment.current?.setErrors(validationErros);
			}
			const { titleMessage, contentMessage } = returnMessageError(errors);
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
			});
		}
	};

	const title = rows && rows.length > 1 ? "Deseja cancelar as parcelas selecionadas?" : "Deseja cancelar a parcela?";
	const valueInfo = row
		? `Parcela nº ${row.number} - ${globalFormatCurrencyBRL(row.amount)}`
		: rows && rows.length === 1
		? `Parcela nº ${rows[0].number} - ${globalFormatCurrencyBRL(rows[0].amount)}`
		: `${rows?.length} parcelas selecionadas`;

	return (
		<Dialog open={isOpenModal} onClose={isClose} maxWidth="sm" fullWidth>
			<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
				<Typography variant="h6" fontWeight={400} color="primary.main">
					{title}
				</Typography>
				<IconButton onClick={isClose}>
					<Close />
				</IconButton>
			</DialogTitle>

			<DialogContent sx={{ px: 4, pt: 1 }}>
				<Typography variant="body2" color="text.secondary" mb={2}>
					{valueInfo}
				</Typography>

				<VForm
					ref={formRefInstallment}
					onSubmit={handleSaveInstallmentCancel}
					onPointerEnterCapture={() => {}}
					onPointerLeaveCapture={() => {}}
					placeholder=""
				>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<VTextField name="obs" label="Motivo do cancelamento" fullWidth multiline rows={3} />
						</Grid>
					</Grid>
				</VForm>
			</DialogContent>

			<DialogActions sx={{ px: 4, pb: 3, pt: 1 }}>
				<Button variant="outlined" onClick={isClose} disabled={isLoading}>
					Voltar
				</Button>
				<Button variant="contained" color="primary" startIcon={<Cancel />} onClick={() => formRefInstallment.current?.submitForm()} disabled={isLoading}>
					Confirmar
				</Button>
			</DialogActions>
		</Dialog>
	);
};
